import React, { useState, useMemo, useEffect } from "react";
import { ArrowLeft, ArrowRight, AlertTriangle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface DataReviewProps {
  data: any[];
  schema: any;
  onComplete: (validData: any[]) => void;
  onCancel: () => void;
}

const DataReview: React.FC<DataReviewProps> = ({
  data,
  schema,
  onComplete,
  onCancel,
}) => {
  const [localData, setLocalData] = useState(data);
  const [activeTab, setActiveTab] = useState<"VALID" | "INVALID">("INVALID");
  
  // FIX: Store the temporary value while typing so we don't re-render the whole table on every keystroke
  const [editingCell, setEditingCell] = useState<{
    rowIdx: number;
    key: string;
    value: string;
  } | null>(null);
  
  const [showInvalidRowsPopup, setShowInvalidRowsPopup] = useState(false);

  const { validRows, invalidRows, validationErrors } = useMemo(() => {
    const valid: any[] = [];
    const invalid: any[] = [];
    const errors: { [rowIdx: number]: { [key: string]: string } } = {};

    localData.forEach((row, idx) => {
      try {
        const validRow = schema.validateSync(row, { abortEarly: false });
        valid.push({ ...validRow, _originalIdx: idx });
      } catch (err: any) {
        invalid.push({ ...row, _originalIdx: idx });
        const rowErrors: { [key: string]: string } = {};

        if (err.inner) {
          err.inner.forEach((error: any) => {
            if (error.path) {
              rowErrors[error.path] = error.message;
            }
          });
        }
        errors[idx] = rowErrors;
      }
    });

    return { validRows: valid, invalidRows: invalid, validationErrors: errors };
  }, [localData, schema]);

  useEffect(() => {
    if (
      invalidRows.length === 0 &&
      activeTab === "INVALID" &&
      validRows.length > 0
    ) {
      setActiveTab("VALID");
    }
  }, [invalidRows.length, validRows.length, activeTab]);

  // Save single cell edit
  const commitCellEdit = (rowIdx: number, path: string[], value: string) => {
    const newData = [...localData];
    const row = JSON.parse(JSON.stringify(newData[rowIdx])); // Deep clone to be safe

    let current = row;
    for (let i = 0; i < path.length - 1; i++) {
      if (current[path[i]] === undefined || current[path[i]] === null) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;

    newData[rowIdx] = row;
    setLocalData(newData);
  };

  const handleBlur = () => {
    if (editingCell) {
      commitCellEdit(editingCell.rowIdx, editingCell.key.split("."), editingCell.value);
      setEditingCell(null);
    }
  };

  // NEW FUNCTION: Apply text to the current row AND all empty rows in the same column
  const handleApplyToAllEmpty = () => {
    if (!editingCell) return;
    
    const path = editingCell.key.split(".");
    const valToApply = editingCell.value;

    const newData = localData.map((row, idx) => {
      const newRow = JSON.parse(JSON.stringify(row));
      let current = newRow;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (current[path[i]] === undefined || current[path[i]] === null) {
          current[path[i]] = {};
        }
        current = current[path[i]];
      }
      
      const targetKey = path[path.length - 1];
      const existingValue = current[targetKey];

      // Update if it's the cell we are currently editing, OR if it's empty
      if (
        idx === editingCell.rowIdx ||
        existingValue === "" ||
        existingValue === null ||
        existingValue === undefined
      ) {
        current[targetKey] = valToApply;
      }
      return newRow;
    });

    setLocalData(newData);
    setEditingCell(null); 
  };

  const flattenRow = (row: any, prefix = ""): { [key: string]: any } => {
    let result: { [key: string]: any } = {};
    for (const key in row) {
      if (key === "_originalIdx") continue;
      if (typeof row[key] === "object" && row[key] !== null) {
        const nested = flattenRow(row[key], prefix + key + ".");
        result = { ...result, ...nested };
      } else {
        result[prefix + key] = row[key];
      }
    }
    return result;
  };

  const headers = useMemo(() => {
    const allKeys = new Set<string>();
    localData.forEach((row) => {
      const flat = flattenRow(row);
      Object.keys(flat).forEach((k) => allKeys.add(k));
    });
    return Array.from(allKeys).sort();
  }, [localData]);

  const submitValidData = () => {
    const cleanData = validRows.map((r) => {
      const copy = { ...r };
      delete copy._originalIdx;
      return copy;
    });
    onComplete(cleanData);
  };

  const handleNext = () => {
    if (invalidRows.length > 0) {
      setShowInvalidRowsPopup(true);
    } else {
      submitValidData();
    }
  };

const renderTable = (rows: any[], isInvalid: boolean) => (

   <div id="tour-review-table" className="overflow-auto h-[58vh] w-full mt-[2vh] border-t border-gray-200">
      <table className="w-full text-left border-collapse min-w-max bg-[#fff]">
<thead className="sticky top-0 bg-[#fff] z-30">
  {/* Removed conflicting border-b classes from the tr */}
  <tr className="bg-[#fff] text-[1.125vw]">
    
    <th className="py-[2vh] px-[1vw] font-inter font-[500] w-[3vw] text-center text-[#000] border-r border-[#CFD6DC] shadow-[inset_0_-2px_0_0_#CFD6DC]">    
      #
    </th>
    
    {headers.map((h) => (
      <th
        key={h}
        className="py-[4vh] px-[1vw] font-inter font-[500] text-[#000] capitalize border-r border-[#CFD6DC] shadow-[inset_0_-2px_0_0_#CFD6DC] last:border-r-0"    
      >
        {h.split(".").pop()?.replace(/_/g, " ")}
      </th>
    ))}
    
  </tr>
</thead>
        <tbody className="text-[1.125vw] text-[#000]">
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={headers.length + 1}
                className="py-[6vh] text-center text-[#000] text-[1.125vw]"
              >
                {isInvalid
                  ? "No errors found! Great job."
                  : "No valid rows yet. Fix errors to move them here."}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const originalIdx = row._originalIdx;
              const flatRow = flattenRow(row);
              const rowErrors = validationErrors[originalIdx] || {};

              return (
                <tr
                  key={originalIdx}
                  className={`border-b border-[#CFD6DC] hover:bg-[rgba(239,246,255,1)] transition-colors ${
                    editingCell?.rowIdx === originalIdx
                      ? "ai-review-row--editing"
                      : ""
                  }`}
                >
                  <td className="py-[1.5vh] px-[1vw] border-r border-[#CFD6DC] text-center text-[#000] font-inter font-[400]">
                    {originalIdx + 1 < 10
                      ? `0${originalIdx + 1}`
                      : originalIdx + 1}
                  </td>

            {headers.map((header) => {
                    const value = flatRow[header] || "";
                    const fieldName = header.split(".").pop() || "";
                    
                    let error = rowErrors[header];
                    if (isInvalid && (value === "" || value === null || value === undefined)) {
                      error = error || `${fieldName.replace(/_/g, " ")} is required`;
                    }

                    const isEditing =
                      editingCell?.rowIdx === originalIdx &&
                      editingCell?.key === header;
                    const isError = error && isInvalid;

                    return (
                      <td
                        key={header}
                       
                        className={`p-[0vw] relative min-w-[8.3vw] cursor-pointer group font-inter font-[400] transition-all last:border-r-0 border-r border-b ${
                          isEditing 
                            ? "bg-[#eff6ff] border-transparent outline outline-[2px] outline-[#374AF2] z-20" 
                            : isError 
                              ? "bg-red-50/20 border-transparent outline outline-[1px] outline-[#FC0E0E] z-10" 
                              : "border-[#CFD6DC]"
                        }`}
                        onClick={() => {
                          if (!isEditing) {
                            setEditingCell({ rowIdx: originalIdx, key: header, value: value });
                          }
                        }}
                      >
                   

                        <div className="flex flex-col items-center justify-center py-[0.50vh] px-[0.5vw] relative z-20 h-full w-full">
                          {isEditing ? (
                            <div className="w-full flex flex-col gap-[0.5vh]">
                              <input
                                autoFocus
                                className="w-full bg-[#fff] px-[0.5vw] py-[0.25vh] text-[0.85vw] rounded border border-[#374AF2] focus:outline-none text-gray-900 text-center "
                                value={editingCell.value}
                                onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                onBlur={handleBlur}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.currentTarget.blur();
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()} 
                                onClick={handleApplyToAllEmpty}
                                className="ai-review-apply-button text-[0.65vw] bg-blue-100 text-[#374AF2] hover:bg-[#374AF2] hover:text-[#fff] transition-colors py-[0.25vh] px-[0.5vw] rounded w-full font-medium"
                              >
                                Apply to all empty
                              </button>
                            </div>
                          ) : (
                            <>
                              {isError ? (
                                <>
                                  <span className="text-[0.75vw] text-[#000] font-inter font-[400] truncate w-full text-center">
                                    {value || `Enter ${fieldName.replace(/_/g, " ").toLowerCase()}`}
                                  </span>
                                  <span className="text-[0.65vw] text-[#FC0E0E] flex items-center justify-center gap-[0.25vw] mt-[0.13vh] w-full truncate font-inter font-[400]">
                                    <AlertTriangle className="w-[0.75vw] h-[0.75vw] flex-shrink-0" />{" "}
                                    {error}
                                  </span>
                                </>
                              ) : (
                                <div className="text-[1.125vw] text-[#000] font-inter font-[400] truncate w-full px-[0.5vw] py-[0.25vh] text-center">
                                  {value ? value : <span className="text-[#000] italic">-</span>}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="pt-[1vh] px-6 flex-1 overflow-y-auto flex flex-col gap-[1.50vw] col-span-3 w-full h-full"
      >
        <div id="tour-review-header" className="px-3 pb-3 bg-[#fff] border border-[#CFD6DC] rounded-[0.75vw] p-[1.50vw] pt-3 flex-shrink-0">
          <h2 className="text-[1.45vw] font-inter font-[400] mb-[0.75vh]">
            Review & Fix Data Issues
          </h2>
          <p className="text-[1.125vw] text-[#000] font-inter font-[400]">
            {invalidRows.length > 0
              ? "We found some missing information. Click the red fields to enter the required details."
              : "All rows are valid. You can continue to the next step."}
          </p>
        </div>

        <div className="border border-[#CFD6DC] rounded-[0.75vw] bg-[#fff] flex flex-col flex-1 overflow-hidden">
          <div className="flex  px-[1.50vw] pt-[2.5vw] bg-gray-50/20">
            <button
              id="tour-review-errors-tab"
              type="button"
              onClick={() => setActiveTab("INVALID")}
              className={`px-[1.50vw] py-[1vh] font-medium text-[1.125vw] transition-colors border-b-2 ${
                activeTab === "INVALID"
                  ? "text-[#374AF2] border-[#374AF2] bg-[#fff]"
                  : "text-[#000] border-gray-300 hover:text-[#374AF2]/80"
              }`}
            >
              Errors ({invalidRows.length})
            </button>
            <button
              id="tour-review-valid-tab"
              type="button"
              onClick={() => setActiveTab("VALID")}
              className={`px-[1.50vw] py-[1vh] font-medium text-[1.125vw] transition-colors border-b-2 ${
                activeTab === "VALID"
                  ? "text-[#374AF2] border-[#374AF2] bg-[#fff]"
                  : "text-[#000] border-gray-300 hover:text-[#374AF2]/80"
              }`}
            >
              Valid ({validRows.length})
            </button>
          </div>

          {activeTab === "INVALID"
            ? renderTable(invalidRows, true)
            : renderTable(validRows, false)}

          
        </div>
        
      </motion.div>
      <div className="ai-wizard-actions flex justify-end gap-[1vw] mt-[1.5vh] mr-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-[2.50vw] py-[1.5vh] border-[0.5px] border-[#3E3E3E] rounded-[0.5vw] font-medium flex items-center justify-center gap-[0.50vw] text-[1.125vw] hover:bg-gray-50 transition-colors w-[13vw] text-[#000]"
            >
              <ArrowLeft className="w-[1.25vw] h-[1.25vw]" /> Back
            </button>
            <button
              id="tour-review-next"
              type="button"
              onClick={handleNext}
              className="px-[2.50vw] py-[1.5vh] bg-[#374AF2] text-[#fff] rounded-[0.5vw] font-medium flex items-center justify-center gap-[0.50vw] text-[1.125vw] hover:bg-blue-700 transition-colors w-[13vw]"
            >
              Next <ArrowRight className="w-[1.25vw] h-[1.25vw]" />
            </button>
          </div>

      <AnimatePresence>
        {showInvalidRowsPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="ai-invalid-modal__backdrop fixed inset-0 bg-[#000]/20 flex items-center justify-center z-50 p-[2vw]"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: "2vh" }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: "2vh" }}
              className="ai-invalid-modal bg-[#fff] rounded-[1.25vw] p-[2.50vw] max-w-[46vw] w-full relative"
            >
              <button
                onClick={() => setShowInvalidRowsPopup(false)}
                className="ai-invalid-modal__close absolute top-[1vw] right-[1.50vw] text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-[2vw] h-[2vw] stroke-[1.5] text-[#949494]" />
              </button>

              <div className="ai-invalid-modal__heading flex items-center gap-[1.25vw] mb-[2vh] w-full justify-center">
                <div className="ai-invalid-modal__icon w-[4vw] h-[4vw] bg-[#FFB50926] rounded-[0.75vw] flex items-center justify-center">
                 <svg 
  className="w-[2.2vw] h-[2.2vw] text-[#FFB509]" 
  viewBox="0 0 24 24" 
  fill="none" 
  xmlns="http://www.w3.org/2000/svg"
>
  <defs>
    <mask id="exclamation-cutout">
      {/* The white background tells the mask to show everything... */}
      <rect width="100%" height="100%" fill="white" />
      {/* ...and the black lines tell the mask what to erase (punch a hole) */}
      <path d="M12 9v4" stroke="black" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1.25" fill="black" />
    </mask>
  </defs>
  
  {/* The solid triangle, painted with your text color, with the mask applied */}
  <path 
    d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" 
    fill="currentColor" 
    mask="url(#exclamation-cutout)"
  />
</svg>
                </div>
                <h2 className="ai-invalid-modal__title text-[1.75vw] font-montserrat font-[600] text-[#FAAF00]">
                  Invalid Rows Detected
                </h2>
              </div>

              <div className="ai-invalid-modal__body space-y-[1vh] mb-[6vh]">
                <p className="text-[1.25vw] text-[#000] font-inter font-[400] leading-snug">
                  {invalidRows.length} rows contain errors in your file. Choose
                  how you want to proceed.
                </p>
                <p className="text-[1.25vw] text-[#4A5565] font-inter font-[400] leading-snug">
                  You can review and correct errors, or continue by skipping
                  invalid rows.
                </p>
              </div>

              <div className="ai-invalid-modal__actions flex gap-[1vw]">
                <button
                  onClick={() => {
                    setShowInvalidRowsPopup(false);
                    submitValidData();
                  }}
                  className="ai-invalid-modal__secondary flex-1 py-[1.4vw] border border-[#C2C2C2] rounded-[0.75vw] text-[1.4vw] font-inter font-[400] hover:bg-gray-50 transition-colors text-[#000]"
                >
                  Skip Invalid Rows
                </button>
                <button
                  onClick={() => setShowInvalidRowsPopup(false)}
                  className="ai-invalid-modal__primary flex-1 py-[1.4vw] bg-[#374AF2] text-[#fff] rounded-[0.75vw] text-[1.4vw] font-inter font-[400] hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Review & Fix Errors
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DataReview;
